import { useMediaQuery, useTheme } from '@mui/material';
import { LazyEventCard } from '@overview/routes/Overview/LazyEventCard';
import { EmblaCarouselType } from 'embla-carousel';
import { UseEmblaCarouselType } from 'embla-carousel-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

type OverviewEventsCarouselProps = {
    slides: number;
    slidesToShow?: number;
    onSlideChange?: (newIndex: number) => void;
    emblaHook: UseEmblaCarouselType;
};

export const OverviewEventsCarousel = ({
    slides,
    slidesToShow = 1,
    onSlideChange,
    emblaHook,
}: OverviewEventsCarouselProps) => {
    const [slidesInView, setSlidesInView] = useState<number[]>([]);

    const [emblaRef, emblaApi] = emblaHook;

    const predeterminedArray = useMemo(() => Array.from({ length: slides }), [slides]);

    const theme = useTheme();
    const isMdOrAbove = useMediaQuery(theme.breakpoints.up('md'));

    const updateSlidesInView = useCallback((emblaApi: EmblaCarouselType) => {
        setSlidesInView((slidesInView) => {
            if (slidesInView.length === emblaApi.slideNodes().length) {
                emblaApi.off('slidesInView', updateSlidesInView);
            }

            const inView = emblaApi.slidesInView().filter((index) => !slidesInView.includes(index));
            return slidesInView.concat(inView);
        });
    }, []);

    useEffect(() => {
        if (emblaApi) {
            const onSelect = () => {
                const selectedIndex = emblaApi.selectedScrollSnap();
                onSlideChange?.(selectedIndex);
            };
            updateSlidesInView(emblaApi);

            emblaApi.on('select', onSelect);
            emblaApi.on('slidesInView', updateSlidesInView);
            emblaApi.on('reInit', updateSlidesInView);

            return () => {
                emblaApi.off('select', onSelect);
                emblaApi.off('slidesInView', updateSlidesInView);
                emblaApi.off('reInit', updateSlidesInView);
            };
        }
    }, [emblaApi, onSlideChange]);

    return (
        <div className="relative overflow-hidden" ref={emblaRef}>
            <div className="flex touch-pan-y touch-pinch-zoom">
                {predeterminedArray.map((_, index) => (
                    <div
                        key={`slide-${index}`}
                        className="pl-[10px]"
                        style={{ flex: `0 0 ${100 / slidesToShow - (isMdOrAbove ? 5 : 10)}%` }}
                    >
                        <LazyEventCard key={`lazy-${index}`} index={index} inView={slidesInView.indexOf(index) > -1} />
                    </div>
                ))}
            </div>
        </div>
    );
};
